import videojs from 'video.js';
import 'videojs-overlay';
import 'videojs-hotkeys';
// Import CSS files as raw strings to get access to css text
import videojsCSS from 'video.js/dist/video-js.css?raw';
import componentCSS from './component-style.css?raw';
// Import template as raw HTML string
import templateHTML from './template.html?raw';
// Import font files as bundled URLs - Vite will include them in the build
import videoJsFontUrl from 'video.js/dist/font/VideoJS.woff?url';
import ciscoSansRegularUrl from '../assets/fonts/CiscoSans/CiscoSansTTRegular.woff2?url';
import ciscoCuiFontUrl from '../assets/fonts/CiscoSans/cui-font.woff2?url';
// Import overlay icons as bundled URLs — needed because CSS is loaded as a raw string in Shadow DOM
import iconInfoDark  from '../assets/icons/info-icon-dark.png?url';
import iconInfoLight from '../assets/icons/info-icon-light.png?url';
import iconThumbsUp from '../assets/icons/thumb-up.svg?url';
import iconThumbsDown from '../assets/icons/thumb-down.svg?url';
import iconGlobe from '../assets/icons/globe_black.svg?url';
import { getTimestamp, deviceType, browserType, secondsTimeSpanToHMS } from './audit-helpers.js';
import { ConfigManager } from './config.js';

class CdamModularVideoPlayer extends HTMLElement {
  // Static cache shared across ALL instances
  static cachedStyleSheet = null;
  static cachedTemplateContent = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // Audit tracking state
    this.trackingObj = {};
    this.firstPlay   = true;

    // Resolved in connectedCallback / SSO flow
    this.config          = null;
    this.assetId         = '';
    this.videoUrl        = '';

    // SSO auth state
    this.isAuthenticated = false;
    this.accessToken     = '';
    this.userId          = '';  // resolved by initSSO → userinfo API (ccoid)

    // Audit metadata
    this.tenant      = 'CX';
    this.sessionType = '';   // TODO: set from asset details API when available

    // Progress tracking
    this.playcount       = 0;
    this.pauseSeeking    = false;
    // client-id and experience-id are fixed for the modular player — not read from attributes
    this.clientParam     = 'CX';
    this.expParam        = 'GR-Video';

    // Captions / text-track state
    this.vttUrl                = '';
    this.video_source_language = '';
    this.languages = [
      {'label': 'EN US', value: 'en'},
      {'label': 'Chinese (中文)', value: 'zh-CN'},
      {'label': 'Japanese (日本語)', value: 'ja'},
      {'label': 'Portuguese (Português)', value: 'pt-PT'},
      {'label': 'Korean (한국어 )', value: 'ko'},
      {'label': 'Spanish (Español)', value: 'es'},
      {'label': 'French (Français)', value: 'fr'}
    ];

    // Overlay state
    this.overlayLinks = [];

    // Language button state — the globe button is created asynchronously in the
    // 'loadstart' handler while translation availability comes from the async
    // assetdetails API, so the two can resolve in either order. This flag holds
    // the desired visibility so whichever finishes last applies it correctly.
    this.languageMenuButton = null;
    this._translationAvailable = false;

    // Feedback state — _liked is true (like), false (dislike) or null (no vote)
    this.feedbackObj = { _liked: null };
    this._feedbackMessageTimer = null;
  }

  async connectedCallback() {
    // WEM host pages expose window.cdcext.customEnvironment ('dev' | 'stage' | 'prod').
    // Fall back to 'localhost' when running locally, otherwise default to 'prod'.
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const env = window.cdcext?.customEnvironment
             || (isLocal ? 'localhost' : 'prod');
    this.config = ConfigManager.retrieveConfig(env);

    // WEM only passes asset-id
    this.assetId = this.getAttribute('asset-id') || '';

    // Construct full video URL from config
    this.videoUrl = this.config.video_base_url + this.assetId
                  + '&client=' + this.clientParam
                  + '&exp='    + this.expParam;

    // Captions VTT endpoint
    this.vttUrl = this.config.vtt_base_url + this.assetId;

    this.loadStyles();
    this.loadTemplate();
    this.initializeVideoPlayer();
    this.getAssetDetails();    // async fetch for overlay data — doesn't need to block player load
    this.loadPlayer(null);     // sets src → begins video buffering
    this.setupAuditTracking();
    this.setupFeedbackButton(); // wire like / dislike / share icons in the feedback bar
    this.setupLanguageIcon();   // wire language selector in the control bar
    await this.initSSO();      // auth flow in background → seeks to resume position if authenticated
    this.render();
  }


  loadStyles() {
    if (CdamModularVideoPlayer.cachedStyleSheet) {
      this.applyStyles();
      return;
    }
    this.initializeStyles();
    this.applyStyles();
  }

  initializeStyles() {
    // Extract @font-face rules — must be in global scope
    const fontFaceRegex = /@font-face\s*\{[^}]+\}/gs;
    const fontFaces = videojsCSS.match(fontFaceRegex) || [];
    const videojsCssWithoutFonts = videojsCSS.replace(fontFaceRegex, '');

    // Replace data: URLs with bundled font URL (imported via Vite)
    // Avoids slow CDN requests AND CSP violations from data: URLs
    const secureFontFaces = fontFaces.map(fontFace => {
      return fontFace.replace(
        /url\(data:application\/font-woff[^)]+\)/g,
        `url(${videoJsFontUrl})`
      );
    });

    // Add secure @font-face to global scope (only once)
    if (secureFontFaces.length > 0 && !document.getElementById('videojs-font-global')) {
      const globalFontStyle = document.createElement('style');
      globalFontStyle.id = 'videojs-font-global';
      globalFontStyle.textContent = secureFontFaces.join('\n');
      document.head.appendChild(globalFontStyle);
    }

    // Add CUI icon font @font-face to global scope — icon fonts need global registration
    // to render reliably in Shadow DOM across all browsers.
    if (!document.getElementById('cui-font-global')) {
      const cuiFontStyle = document.createElement('style');
      cuiFontStyle.id = 'cui-font-global';
      cuiFontStyle.textContent = `@font-face { font-family: 'cdam-cui-font'; src: url(${ciscoCuiFontUrl}) format('woff2'); }`;
      document.head.appendChild(cuiFontStyle);
    }

    const ciscoSansCSS = `
      @font-face {
        font-family: 'CiscoSans';
        src: url(${ciscoSansRegularUrl}) format('woff2');
        font-style: normal;
        font-weight: 400;
        font-display: swap;
      }
    `;

    // Create shared CSSStyleSheet object
    const combinedCSS = videojsCssWithoutFonts + '\n\n' + componentCSS + '\n\n' + ciscoSansCSS;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(combinedCSS);
    CdamModularVideoPlayer.cachedStyleSheet = sheet;
  }

  applyStyles() {
    this.shadowRoot.adoptedStyleSheets = [CdamModularVideoPlayer.cachedStyleSheet];
  }

  loadTemplate() {
    if (CdamModularVideoPlayer.cachedTemplateContent) {
      this.applyTemplate();
      return;
    }
    this.initializeTemplate();
    this.applyTemplate();
  }

  initializeTemplate() {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = templateHTML;
    const template = tempDiv.querySelector('template');
    CdamModularVideoPlayer.cachedTemplateContent = template.content;
  }

  applyTemplate() {
    const content = CdamModularVideoPlayer.cachedTemplateContent.cloneNode(true);
    this.shadowRoot.appendChild(content);

    const likeImg = this.shadowRoot.querySelector('#cdam-like-icon');
    if (likeImg) likeImg.src = iconThumbsUp;
    const dislikeImg = this.shadowRoot.querySelector('#cdam-dislike-icon');
    if (dislikeImg) dislikeImg.src = iconThumbsDown;
  }

  initializeVideoPlayer() {
    const videoElement = this.shadowRoot.querySelector('#vidMain');

    if (videoElement) {
      this.player = videojs(videoElement, {
        controls: true,
        autoplay: false,
        muted: false,
        preload: 'auto',
        fluid: true,
        responsive: true,
        playbackRates: [0.5, 1, 1.25, 1.5, 1.75, 2],
        controlBar: {
          volumePanel: {
            inline: false
          },
          remainingTimeDisplay: false,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true
        }
      });

      // Space/arrow/M/F/0-9 keyboard shortcuts and double-click fullscreen.
      this.player.hotkeys({ alwaysCaptureHotkeys: true });

      // Register seek buttons and add them to the control bar once the player is ready
      this.player.on('loadstart', () => {
        if (this.player.controlBar.subsCapsButton) {
          this.player.controlBar.subsCapsButton.hide();
        }
        this.closedCaptionButton();
        this.seekButtons();
        this.player.controlBar.addChild('YouTubeCCButton', {}, this.player.controlBar.children().length - 2);
        this.player.controlBar.addChild('SeekBackwardButton', {}, 1);
        this.player.controlBar.addChild('SeekForwardButton', {}, 2);
          // Find index of playback rate button
        const children = this.player.controlBar.children();
        const playbackIndex = children.findIndex(
          child => child.name && child.name() === 'PlaybackRateMenuButton'
        );
        this.languageMenuButton = this.player.controlBar.addChild('LanguageMenuButton', {}, playbackIndex);
        // Apply visibility now that the button exists (assetdetails may have already resolved).
        this._applyLanguageButtonVisibility();
      });
    } else {
      console.error('Video element not found');
    }
  }

  seekButtons() {
    const Button = videojs.getComponent('Button');

    class SeekForwardButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('Seek Forward 15 seconds');
        this.addClass('vjs-seek-forward');
      }

      handleClick() {
        const currentTime = this.player().currentTime();
        this.player().currentTime(currentTime + 15);
      }
    }

    class SeekBackwardButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('Seek Backward 15 seconds');
        this.addClass('vjs-seek-backward');
      }

      handleClick() {
        const currentTime = this.player().currentTime();
        this.player().currentTime(Math.max(0, currentTime - 15));
      }
    }

    videojs.registerComponent('SeekBackwardButton', SeekBackwardButton);
    videojs.registerComponent('SeekForwardButton', SeekForwardButton);
  }

  // Registers the custom single-click CC toggle button
  closedCaptionButton() {
    const Button = videojs.getComponent('Button');
    class YouTubeCCButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText('CC');
        this.addClass('vjs-cc-button');
        this.el().innerHTML = `
          <span class="vjs-icon-placeholder"></span>
        `;
      }

      handleClick() {
        const tracks = this.player().remoteTextTracks();
        let captionsOn = false;

        // Check whether any caption track is currently showing
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' && tracks[i].mode === 'showing') {
            captionsOn = true;
            break;
          }
        }

        if (captionsOn) {
          // Turn CC off — disable all caption tracks, but preserve
          // the selected language by setting it to 'hidden' (not 'disabled')
          // so the language picker can find it again on the next CC-on.
          for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions') {
              tracks[i].mode = tracks[i].mode === 'showing' ? 'hidden' : 'disabled';
            }
          }
        } else {
          // Turn CC on — show the 'hidden' (pre-selected) track, or fall back
          // to the first caption track if none is marked hidden.
          let activated = false;
          for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind === 'captions' && tracks[i].mode === 'hidden') {
              tracks[i].mode = 'showing';
              activated = true;
              break;
            }
          }
          // Fallback: no hidden track — show the first caption track
          if (!activated) {
            for (let i = 0; i < tracks.length; i++) {
              if (tracks[i].kind === 'captions') {
                tracks[i].mode = 'showing';
                break;
              }
            }
          }
        }

        this.toggleClass('vjs-cc-on', !captionsOn);
      }
    }
    videojs.registerComponent('YouTubeCCButton', YouTubeCCButton);
  }

  // Builds one remote text track
  loadTextTracks() {
    // Remove any existing remote text tracks to avoid duplicates on re-load
    const existing = this.player.remoteTextTracks();
    for (let i = existing.length - 1; i >= 0; i--) {
      this.player.removeRemoteTextTrack(existing[i]);
    }

    const sourceLang = (this.video_source_language || 'en').toLowerCase();
    // Put source language first so it becomes the default (index-0) track
    const sortedLanguages = [
      ...this.languages.filter(l => l.value.toLowerCase() === sourceLang),
      ...this.languages.filter(l => l.value.toLowerCase() !== sourceLang)
    ];
    sortedLanguages.forEach(lang => {
      const isSource = lang.value.toLowerCase() === sourceLang;
      this.player.addRemoteTextTrack({
        kind: 'captions',
        label: lang.label,
        srclang: lang.value,
        src: isSource ? this.vttUrl : `${this.vttUrl}&lang=${lang.value}`,
        default: false
      }, false);
    });
  }

  // Sets video source and optionally seeks to a resume position
  loadPlayer(startTime) {
    if (!this.videoUrl) return;
    if (!this.player.src()) {
      this.player.src({ src: this.videoUrl, type: 'video/mp4' });
    }
    if (startTime) {
      this.player.one('loadedmetadata', () => {
        this.player.currentTime(parseFloat(startTime));
      });
    }
  }

  async initSSO() {
    // Try to get SSO token from WEM host page
    const token = await window.cdc?.sso?.hasAuthHeader?.() ?? false;

    if (token) {
      this.accessToken = token;
      console.log('token fetched, fetching user info...');

      try {
        // Resolve userId (ccoid) via the userinfo API using the SSO token
        const res = await fetch(this.config.restapi_base_url + '/cdamplayer/v1/oauth/userinfo', {
          headers: { 'Authorization': this.accessToken }
        });
        if (res.ok) {
          const userInfo = await res.json();
          this.userId = userInfo.ccoid || '';
        }
      } catch (err) {
        console.warn('getUserInfo failed:', err.message);
        // userId remains '' — falls through to unauthenticated path below
      }

      if (this.userId) {
        // Both token and userId present — fully authenticated
        this.isAuthenticated = true;
        await this.init(true);
        return;
      }

      // Token received but no userId resolved — treat as unauthenticated
      console.warn('SSO token present but no userId resolved, proceeding unauthenticated');
    }

    // Not authenticated
    this.isAuthenticated = false;
    await this.init(false);
  }

  async init(isAuthenticated) {
    if (this.userId) {
      await this.getProgress();
      this.getFeedback();
    }
    if (isAuthenticated) {
      this.buildAuditObject();
    } else {
      // Video already loading from beginning, build the audit object
      setTimeout(() => this.buildAuditObject(), 500);
    }
  }

  async getProgress() {
    // Public endpoint — no Authorization header needed
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/progress/${this.userId}/${this.assetId}`;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`getProgress HTTP ${response.status}`);
      const data = await response.json();
      if (data && data.time) {
        this._showResumeDialog(data.time);
      }
    } catch (err) {
      console.warn('Failed to fetch progress, loading from beginning:', err);
    }
  }

  _showResumeDialog(resumeTime) {
    const dialog   = this.shadowRoot.querySelector('#resume-dialog');
    const backdrop = this.shadowRoot.querySelector('.resume-backdrop');
    const yesBtn   = this.shadowRoot.querySelector('#resume-yes');
    const noBtn    = this.shadowRoot.querySelector('#resume-no');

    backdrop.classList.add('active');
    dialog.show();

    const closeDialog = () => {
      dialog.close();
      backdrop.classList.remove('active');
    };

    yesBtn.onclick = () => {
      closeDialog();
      if (this.player.readyState() >= 1) {
        this.player.currentTime(parseFloat(resumeTime));
      } else {
        this.player.one('loadedmetadata', () => this.player.currentTime(parseFloat(resumeTime)));
      }
      this.player.play();
      this.deleteUserProgress();
      this.trackingObj['progress'] = {
        time:    secondsTimeSpanToHMS(resumeTime).substr(2, 5),
        resumed: 'yes'
      };
      this.callAuditAPI('resume');
    };

    noBtn.onclick = () => {
      closeDialog();
      this.player.play();
      this.deleteUserProgress();
      this.trackingObj['progress'] = {
        time:    secondsTimeSpanToHMS(resumeTime).substr(2, 5),
        resumed: 'no'
      };
      this.callAuditAPI('resume');
    };
  }

  deleteUserProgress() {
    if (!this.userId) return;
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/progress/${this.userId}/${this.assetId}`;
    fetch(url, { method: 'DELETE' })
      .then(response => { if (!response.ok) throw new Error(`HTTP ${response.status}`); })
      .then(() => console.log('Progress deleted'))
      .catch(err => console.warn('Failed to delete progress:', err.message));
  }

  saveUserProgress() {
    if (!this.userId) {
      console.log('No authenticated userId resolved, skipping progress save');
      return;
    }

    const data = {
      userid:        this.userId,
      assetid:       this.assetId,
      createdDate:   getTimestamp() + '.000Z',
      time:          this.player.currentTime(),
      experience_id: this.expParam,
      client_id:     this.clientParam
    };

    // Public endpoint — no Authorization header needed
    const apiUrl = this.config.restapi_base_url + '/cdamplayer/v1/public/progress';

    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(() => console.log('Save progress successful'))
    .catch(error => console.error('Failed to save user progress:', error.message));
  }

  getAssetDetails() {
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/assetdetails/${this.assetId}`;
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`Asset details fetch failed: HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        // Record translation availability and apply it (button may not exist yet;
        // the 'loadstart' handler re-applies once it's created).
        this._translationAvailable = data?.translation_available === true;
        this._applyLanguageButtonVisibility();

        // Determine caption source language (defaults to 'en' if unknown/unmatched)
        const shortCode = data.language ? data.language.toLowerCase() : 'en';
        const match = this.languages.find(l => l.value.toLowerCase().startsWith(shortCode));
        this.video_source_language = match ? match.value : shortCode;
        this.loadTextTracks();

        // Capture overlay links and initialise the overlay plugin
        this.overlayLinks = (data.overlays && Array.isArray(data.overlays)) ? data.overlays : [];
        this.setupOverlays();
      })
      .catch(err => {
        console.warn('Could not fetch asset details:', err.message);
        this.video_source_language = 'en';
        this.loadTextTracks();
      });
  }

  // Shows or hides the globe (language) button based on the latest known
  // translation availability. Safe to call before the button exists.
  _applyLanguageButtonVisibility() {
    if (!this.languageMenuButton) return;
    this._translationAvailable
      ? this.languageMenuButton.show()
      : this.languageMenuButton.hide();
  }

  // Converts "HH:MM:SS" or "MM:SS" timestamp string to total seconds (number).
  _convertToSeconds(str) {
    const parts = str.split(':');
    let seconds = 0, multiplier = 1;
    while (parts.length > 0) {
      seconds += multiplier * parseInt(parts.pop(), 10);
      multiplier *= 60;
    }
    return seconds;
  }

  // Ensures the URL has a protocol prefix.
  _isHttpValidURL(str) {
    if (str.indexOf('http://') === -1 && str.indexOf('https://') === -1) {
      str = 'https://' + str;
    }
    return str;
  }

  // Transforms raw API overlay objects into the format expected by videojs-overlay.
  setupOverlays() {
    if (!this.player || !this.overlayLinks || this.overlayLinks.length === 0) return;

    const overlays = this.overlayLinks.map(ov => {
      const startSecs  = this._convertToSeconds(ov.overlay_start_time);
      const url        = this._isHttpValidURL(ov.overlay_url);
      const background = (ov.overlay_background || 'light').toLowerCase();
      const isDark     = background === 'dark';
      const infoIcon   = isDark ? iconInfoLight : iconInfoDark;

      return {
        content: `<a class="cdam-overlay-link" href="${url}" target="_blank" rel="noopener noreferrer">` +
                   `<span class="overlay-info-img" style="background-image: url(${infoIcon})"></span>` +
                   `<span class="overlay-text-span">${ov.overlay_title}</span>` +
                 `</a>`,
        start:   startSecs,
        end:     startSecs + 10,  // each overlay is shown for exactly 10 seconds
        align:   'top-right',
        class:   'overlay-' + background
      };
    });

    this.player.overlay({
      content:  '',
      overlays: overlays
    });

    // Hide any visible overlays when the video ends.
    // videojs-overlay hides overlays via 'timeupdate' (time >= end). When the video
    // ends, timeupdate stops firing, so an overlay whose end time exceeds the video
    // duration stays on screen. The 'ended' listener cleans this up.
    if (!this._overlayEndedListenerAdded) {
      this._overlayEndedListenerAdded = true;
      this.player.on('ended', () => {
        this.shadowRoot.querySelectorAll('.vjs-overlay:not(.vjs-hidden)').forEach(el => {
          el.classList.add('vjs-hidden');
        });
      });
    }

    // Event delegation: handle overlay link clicks inside the Shadow DOM.
    // Inline onclick handlers cannot reference instance methods in Shadow DOM,
    // so a single delegated listener on the shadow root is used instead.
    if (!this._overlayClickDelegated) {
      this._overlayClickDelegated = true;
      this.shadowRoot.addEventListener('click', e => {
        const link = e.target.closest('.cdam-overlay-link');
        if (!link) return;

        this.player.pause();
        this.trackingObj['overlay_clicked_time'] = this.player.currentTime();
        this.trackingObj['overlay_title']        = link.querySelector('.overlay-text-span')?.textContent || '';
        this.trackingObj['overlay_url']          = link.href;
        this.callAuditAPI('overlay_click');
        // Clean up overlay-specific fields so they don't appear in unrelated audit calls
        delete this.trackingObj['overlay_clicked_time'];
        delete this.trackingObj['overlay_title'];
        delete this.trackingObj['overlay_url'];
      });
    }
  }

  buildAuditObject() {
    this.trackingObj['transcript_downloaded'] = false;
    this.trackingObj['content_downloaded']    = false;
    this.trackingObj['content_ended']         = false;
    this.trackingObj['content_seeked']        = false;
    this.trackingObj['seek_times']            = [];
    this.trackingObj['content_id']            = this.assetId;
    this.trackingObj['created_date']          = getTimestamp() + '.000Z';
    this.trackingObj['userid']                = this.userId;
    this.trackingObj['client_id']             = this.clientParam;
    this.trackingObj['experience_id']         = this.expParam;
    this.trackingObj['transcript_search']     = [];
    this.trackingObj['device']                = deviceType();
    this.trackingObj['browser']               = browserType();
    this.trackingObj['tenant']                = this.tenant;
    this.trackingObj['location']              = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.trackingObj['new_request']           = true;
    this.trackingObj['session_type']          = this.sessionType;
    this.trackingObj['source']                = window.location.href;
  }

  setupAuditTracking() {
    if (!this.player) return;

    this.buildAuditObject();

    this.player.on('play', () => {
      this.trackingObj['video_length'] = this.player.duration();

      if (this.firstPlay) {
        this.trackingObj['viewed'] = true;
        this.callAuditAPI('play - first view');
        delete this.trackingObj['viewed'];
        this.firstPlay = false;
      }

      // Save progress if resuming after seeking while paused
      if (this.pauseSeeking) {
        if (this.player.remainingTime() != 0) {
          this.saveUserProgress();
        }
        this.pauseSeeking = false;
      }
      this.playcount++;
    });

    this.player.on('pause', () => {
      // Save progress only if not at the end of video
      if (this.player.remainingTime() != 0) {
        this.saveUserProgress();
      }

      const p      = {};
      p['resumed'] = 'no';
      p['time']    = this.player.currentTime().toString();
      this.trackingObj['progress'] = p;
      this.callAuditAPI('pause');
    });

    this.player.on('seeked', () => {
      this.trackingObj['content_seeked'] = true;
      this.trackingObj['seek_times'].push(
        secondsTimeSpanToHMS(this.player.currentTime()).substr(2, 5)  // e.g. "02:35"
      );
      this.callAuditAPI('seek');
      this.trackingObj['content_seeked'] = false;

      // Set flag if seeking while paused (will save progress on next play)
      if (this.player.paused()) {
        this.pauseSeeking = true;
      }

      // Save progress if user seeks while playing (after first play)
      if (this.playcount > 1 && !this.pauseSeeking) {
        if (this.player.remainingTime() != 0) {
          this.saveUserProgress();
        }
      }
    });

    this.player.on('ended', () => {
      this.trackingObj['content_ended'] = true;
      this.callAuditAPI('ended');
      this.deleteUserProgress();
    });
  }

  // Wires the like / dislike / share icons in the feedback bar.
  setupFeedbackButton() {
    const likeBtn    = this.shadowRoot.querySelector('#likevideo_id');
    const dislikeBtn = this.shadowRoot.querySelector('#dislikevideo_id');

    if (likeBtn) {
      likeBtn.addEventListener('click', () => {
        if (this.feedbackObj._liked === true) {
          // Toggle off an existing like
          this.feedbackObj._liked = null;
          this._setVoteSelection(null);
          this._hideFeedbackMessage();
        } else {
          this.feedbackObj._liked = true;
          this._setVoteSelection(true);
          this._showFeedbackMessage('Thanks for your feedback. Your response has been recorded.');
          this.saveFeedback(true);
        }
      });
    }

    if (dislikeBtn) {
      dislikeBtn.addEventListener('click', () => {
        if (this.feedbackObj._liked === false) {
          // Toggle off an existing dislike
          this.feedbackObj._liked = null;
          this._setVoteSelection(null);
          this._hideFeedbackMessage();
        } else {
          this.feedbackObj._liked = false;
          this._setVoteSelection(false);
          this._showFeedbackMessage('Thanks for your feedback. Your response has been recorded.');
          this.saveFeedback(false);
        }
      });
    }
  }

  setupLanguageIcon() {
    const self = this; // capture component instance for use inside inner classes
    const MenuItem = videojs.getComponent('MenuItem');
    const MenuButton = videojs.getComponent('MenuButton');
    const languages = [
      {'label': 'EN US', value: 'en'},
      {'label': 'Chinese (中文)', value: 'zh-CN'},
      {'label': 'Japanese (日本語)', value: 'ja'},
      {'label': 'Portuguese (Português)', value: 'pt-PT'},
      {'label': 'Korean (한국어 )', value: 'ko'},
      {'label': 'Spanish (Español)', value: 'es'},
      {'label': 'French (Français)', value: 'fr'}
    ];
  
    class LanguageMenuButton extends MenuButton {
      constructor(player, options) {
        super(player, options);
        this.controlText('Language');
        this.addClass('vjs-language-button');
        const placeholder = this.el().querySelector('.vjs-icon-placeholder');
        if (placeholder) {
          const img = document.createElement('img');
          img.src = iconGlobe;
          img.className = 'vjs-language-icon';
          placeholder.appendChild(img);
        }
      }

      createItems() {
        return languages.map(lang => {
          return new LanguageMenuItem(this.player_, {
            label: lang.label,
            value: lang.value,
            selected: this.player_.language().toLocaleLowerCase() === lang.value.toLocaleLowerCase(),
            selectable: true
          });
        });
      }
    }
    videojs.registerComponent('LanguageMenuButton', LanguageMenuButton);

    class LanguageMenuItem extends MenuItem {
      constructor(player, options) {
        super(player, options);
        this.value = options.value;
      }

      handleClick() {
        super.handleClick();
        // Set the player UI locale (video.js control labels)
        this.player_.language(this.value);

        // Switch the active caption track to the selected language.
        // Only swap mode when at least one caption track is currently showing
        // (i.e. CC is on). If CC is off, just record the choice so the next
        // CC-on toggle starts with the right track.
        const tracks = this.player_.remoteTextTracks();
        let ccIsOn = false;
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' && tracks[i].mode === 'showing') {
            ccIsOn = true;
            break;
          }
        }
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          if (track.kind !== 'captions') continue;
          const isSelected = track.language.toLowerCase() === this.value.toLowerCase();
          if (ccIsOn) {
            track.mode = isSelected ? 'showing' : 'disabled';
          } else {
            // CC is off — mark the chosen track so it activates when CC is turned on
            track.mode = isSelected ? 'hidden' : 'disabled';
          }
        }

        // Rebuild menu to reflect updated selected state
        this.player_.getChild('controlBar')
          .getChild('LanguageMenuButton')
          .update();

        const shortLang  = this.value.split('-')[0];
        self.trackingObj['transcript_language'] = shortLang;
        self.trackingObj['transcript_language_change'] = true;
        self.callAuditAPI('Language_Change');
      }
    }

    videojs.registerComponent('LanguageMenuButton', LanguageMenuButton);
  }

  // Reflects the current vote in the UI by toggling the selected state on the buttons.
  // liked === true → like selected, false → dislike selected, null → none selected.
  _setVoteSelection(liked) {
    const likeBtn    = this.shadowRoot.querySelector('#likevideo_id');
    const dislikeBtn = this.shadowRoot.querySelector('#dislikevideo_id');
    likeBtn?.classList.toggle('cdam-vote-btn--selected', liked === true);
    dislikeBtn?.classList.toggle('cdam-vote-btn--selected', liked === false);
  }

  // Displays a transient confirmation message in the feedback-message region.
  _showFeedbackMessage(text) {
    const messageEl = this.shadowRoot.querySelector('#cdam-feedback-message');
    if (!messageEl) return;
    messageEl.textContent = text;
    messageEl.hidden = false;

    // Auto-hide after a few seconds so the message doesn't linger.
    clearTimeout(this._feedbackMessageTimer);
    this._feedbackMessageTimer = setTimeout(() => this._hideFeedbackMessage(), 3000);
  }

  _hideFeedbackMessage() {
    const messageEl = this.shadowRoot.querySelector('#cdam-feedback-message');
    if (!messageEl) return;
    clearTimeout(this._feedbackMessageTimer);
    messageEl.hidden = true;
    messageEl.textContent = '';
  }


  // Loads any previously saved feedback for this user + asset and reflects it in the UI.
  getFeedback() {
    if (!this.userId) return;
    const url = this.config.restapi_base_url
              + `/cdamplayer/v1/public/feedback/${this.userId}/${this.assetId}`;
    fetch(url)
      .then(response => {
        if (!response.ok) throw new Error(`getFeedback HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (data && data['userid'] !== undefined) {
          this.feedbackObj._liked = data['_liked'];
          this._setVoteSelection(data['_liked']);
        }
      })
      .catch(err => console.warn('Failed to fetch feedback:', err.message));
  }

  // Persists the user's like / dislike selection.
  saveFeedback(isLiked) {
    // if (!this.userId) return; // skip silently when no authenticated user
    const fbObj = {
      userid:        this.userId ? this.userId : '',
      assetid:       this.assetId,
      _liked:        isLiked,
      time:          this.player.currentTime(),
      created_date:  getTimestamp() + '.000Z',
      experience_id: this.expParam,
      client_id:     this.clientParam
    };
    const url = this.config.restapi_base_url + '/cdamplayer/v1/public/feedback';
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fbObj)
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then(() => console.log('Feedback saved successfully'))
    .catch(err => console.error('Failed to save feedback:', err.message));
  }

  callAuditAPI(eventType) {
    const apiUrl  = this.config.restapi_base_url + '/cdamplayer/v1/audit';
    const headers = { 'Content-Type': 'application/json' };

    // Include Authorization header on authenticated calls
    if (this.accessToken) {
      headers['Authorization'] = this.accessToken;
    }

    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(this.trackingObj)
    })
    .then(response => {
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text}`);
        });
      }
      if (contentType.includes('application/json')) {
        return response.json();
      }
      return response.text();
    })
    .then(() => console.log(`Audit [${eventType}] successful`))
    .catch(error => console.error(`Audit [${eventType}] error:`, error.message));
  }

  // Cleanup when component is removed from the DOM
  disconnectedCallback() {
    if (this.player) {
      this.player.dispose();
    }
  }

  render() {
    // Video player is already rendered and initialized.
    // This method is available for future dynamic updates.
  }
}

customElements.define('cdam-modular-vp', CdamModularVideoPlayer);

